import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { merkeLoginRueckweg } from './auth-rueckweg'

const AUTH_API = 'https://auth-api.arkturian.com'
const STORAGE_KEY = 'ark-auth'

export interface AuthUser {
  id: string
  email: string
  first_name: string
  last_name: string
  is_superadmin: boolean
  roles: { sys: string; role: string; tid: string | null }[]
  /**
   * Profilbild des Anmeldedienstes (Google). Serverseitig laengst vorhanden —
   * `users.picture`, bei jeder Anmeldung aktualisiert und im Zugriffs-Token
   * als `picture`-Angabe mitgeliefert. Das Frontend hat es bisher nur nie
   * gelesen. Fehlt es (Magic-Link, Anmeldung ohne Bild), zeigt die Oberflaeche
   * die Initialen; ein Platzhalter-Bild waere eine Behauptung ueber jemanden,
   * den wir nicht kennen.
   */
  picture?: string | null
}

interface StoredAuth {
  access_token: string
  refresh_token: string
  expires_at: number
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  /** Anmeldung starten. `rueckweg` ist die Stelle, an die der Besucher danach
   *  zurueckkommt — Vorgabe: die Seite, auf der er gerade steht. */
  startGoogleLogin: (rueckweg?: string) => Promise<void>
  handleGoogleCallback: (code: string) => Promise<boolean>
  logout: () => void
  getAccessToken: () => string | null
}

function loadStored(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function saveTokens(accessToken: string, refreshToken: string, expiresIn: number) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    access_token: accessToken,
    refresh_token: refreshToken,
    expires_at: Date.now() + expiresIn * 1000,
  }))
}

function clearStored() { localStorage.removeItem(STORAGE_KEY) }

/** `picture`-Angabe aus dem Zugriffs-Token lesen. Reines Auslesen, KEINE
 *  Pruefung der Signatur — das Bild ist Zierde, kein Zugriffsrecht. */
function pictureFromToken(token: string): string | null {
  try {
    const body = token.split('.')[1]
    if (!body) return null
    const json = atob(body.replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(json) as { picture?: string }
    return claims.picture ?? null
  } catch {
    return null
  }
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

// DEV BYPASS — remove for production
const DEV_USER: AuthUser | null = import.meta.env.DEV ? {
  id: 'dev-bypass',
  email: 'apopovic.aut@gmail.com',
  first_name: 'Alex',
  last_name: 'Popovic',
  is_superadmin: true,
  roles: [],
} : null

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(DEV_USER)
  const [loading, setLoading] = useState(DEV_USER ? false : true)
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>(null)

  const scheduleRefresh = useCallback((expiresAt: number) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    const delay = Math.max(0, expiresAt - Date.now() - 60_000)
    refreshTimer.current = setTimeout(async () => {
      const stored = loadStored()
      if (!stored) return
      try {
        const res = await fetch(`${AUTH_API}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: stored.refresh_token }),
        })
        if (!res.ok) { clearStored(); setUser(null); return }
        const data = await res.json()
        saveTokens(data.access_token, data.refresh_token, data.expires_in)
        scheduleRefresh(Date.now() + data.expires_in * 1000)
      } catch { clearStored(); setUser(null) }
    }, delay)
  }, [])

  const fetchMe = useCallback(async (token: string): Promise<AuthUser | null> => {
    try {
      const res = await fetch(`${AUTH_API}/api/v1/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return null
      const me = await res.json() as AuthUser
      // Zwei Quellen, eine Wahrheit: Liefert `/me` das Bild nicht mit, steht es
      // in der `picture`-Angabe des Tokens (so dokumentiert im auth-api). Erst
      // wenn beide schweigen, gibt es keins — dann zeigen wir Initialen.
      return me.picture ? me : { ...me, picture: pictureFromToken(token) }
    } catch { return null }
  }, [])

  // Restore session
  useEffect(() => {
    const stored = loadStored()
    if (!stored) { setLoading(false); return }

    if (stored.expires_at > Date.now()) {
      fetchMe(stored.access_token).then(u => {
        if (u) { setUser(u); scheduleRefresh(stored.expires_at) }
        else clearStored()
        setLoading(false)
      })
    } else {
      fetch(`${AUTH_API}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: stored.refresh_token }),
      })
        .then(async res => {
          if (!res.ok) throw new Error()
          const data = await res.json()
          saveTokens(data.access_token, data.refresh_token, data.expires_in)
          const u = await fetchMe(data.access_token)
          if (u) { setUser(u); scheduleRefresh(Date.now() + data.expires_in * 1000) }
          else clearStored()
        })
        .catch(() => clearStored())
        .finally(() => setLoading(false))
    }
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current) }
  }, [fetchMe, scheduleRefresh])

  const startGoogleLogin = useCallback(async (rueckweg?: string) => {
    /* Der Weg ueber Google verlaesst die Anwendung ganz — Zustand im Speicher
     * ueberlebt das nicht. Bisher kam der Besucher stur auf /navigate-tile
     * heraus: Wer in einer Art stand und aufs Herz tippte, fand sich danach in
     * der Uebersicht wieder (Alex 2026-08-12). Also merken, wo er war.
     *
     * localStorage, nicht sessionStorage: Manche Browser oeffnen die
     * Google-Seite in einem neuen Kontext, dann ist die Sitzung leer. Mit
     * Zeitstempel, damit ein abgebrochener Anlauf nicht Wochen spaeter noch
     * irgendwohin fuehrt. */
    merkeLoginRueckweg(rueckweg ?? (window.location.pathname + window.location.search))
    const redirectUri = `${window.location.origin}/auth/google/callback`
    const res = await fetch(`${AUTH_API}/api/v1/auth/google/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`)
    if (res.ok) {
      const { url } = await res.json()
      /* In der iOS-App laeuft die Seite in einer WKWebView, und dort blockiert
       * Google den OAuth-Ablauf (`disallowed_useragent`). Kann der Wirt das
       * Systemfenster oeffnen (`auth:native`), uebergeben wir ihm DIESELBE
       * Authorize-URL; er laedt am Ende die Callback-URL mit dem Code zurueck
       * in die WebView, und `handleGoogleCallback` tauscht ihn wie im Browser.
       * Nimmt er nicht an (keine Faehigkeit, kein ACK, keine Bruecke), bleibt
       * es beim Redirect — ein Knopf, der nichts tut, waere schlechter. */
      const uebergeben = await window.tscheppaHostBridge?.requestAuth({
        authorizeUrl: url,
        callbackUrl: redirectUri,
      }) ?? false
      if (!uebergeben) window.location.href = url
    }
  }, [])

  const handleGoogleCallback = useCallback(async (code: string) => {
    const redirectUri = `${window.location.origin}/auth/google/callback`
    const res = await fetch(`${AUTH_API}/api/v1/auth/google/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    })
    if (!res.ok) return false
    const data = await res.json()
    saveTokens(data.access_token, data.refresh_token, data.expires_in)
    if (data.user) {
      setUser(data.user)
      scheduleRefresh(Date.now() + data.expires_in * 1000)
      return true
    }
    return false
  }, [scheduleRefresh])

  const logout = useCallback(() => {
    const stored = loadStored()
    if (stored) {
      fetch(`${AUTH_API}/api/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: stored.refresh_token }),
      }).catch(() => {})
    }
    clearStored()
    setUser(null)
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
  }, [])

  const getAccessToken = useCallback((): string | null => {
    const stored = loadStored()
    if (!stored || stored.expires_at <= Date.now()) return null
    return stored.access_token
  }, [])

  return (
    <AuthContext value={{ user, loading, startGoogleLogin, handleGoogleCallback, logout, getAccessToken }}>
      {children}
    </AuthContext>
  )
}
